import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, RotateCcw, RotateCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NoticeBox } from "@/components/NoticeBox";
import { loadPdfDocument } from "./utils";
import { renderPageThumbnail } from "./pdfThumbnails";
import { moveArranged, removeArranged, turnArranged, type ArrangedPage } from "./mergePagePlan";

type PdfDoc = Awaited<ReturnType<typeof loadPdfDocument>>;

/**
 * Every page of the result in one grid, across all files. Drag a page (or use its arrows) to move it anywhere, even between
 * files; rotate it; delete it. Thumbnails are drawn only when they scroll into view, through one render queue, and each file
 * is opened in pdf.js only while this panel is open. The list is the exact page order of the merged file.
 */
export function MergePageArranger({ files, pages, onChange, onReset, onClose }: {
  files: File[];
  pages: ArrangedPage[];
  onChange: (next: ArrangedPage[]) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const docs = useRef(new Map<File, Promise<PdfDoc | null>>());
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  useEffect(() => {
    const map = docs.current;
    return () => {
      map.forEach((p) => { void p.then((d) => { if (d) void d.loadingTask.destroy(); }); });
      map.clear();
    };
  }, []);

  const docFor = (file: File) => {
    let p = docs.current.get(file);
    if (!p) { p = loadPdfDocument(file).catch(() => null); docs.current.set(file, p); }
    return p;
  };
  const render = (fileIndex: number, index: number) => {
    const run = queue.current.then(async () => {
      const file = files[fileIndex];
      if (!file) return null;
      const d = await docFor(file);
      if (!d) return null;
      try { return await renderPageThumbnail(d, index + 1, 90); } catch { return null; }
    });
    queue.current = run;
    return run;
  };

  const drop = (to: number) => {
    if (dragFrom !== null && dragFrom !== to) onChange(moveArranged(pages, dragFrom, to));
    setDragFrom(null); setDragOver(null);
  };

  // A key that follows the page (not its position), so moving a page keeps its drawn thumbnail instead of redrawing it.
  const seen = new Map<string, number>();
  const keys = pages.map((p) => { const base = `${p.fileIndex}-${p.index}`; const n = seen.get(base) ?? 0; seen.set(base, n + 1); return `${base}-${n}`; });

  return (
    <div className="mt-3 rounded-lg border border-border p-3" data-testid="merge-arranger">
      <div className="flex items-start gap-2">
        <p className="text-sm flex-1 min-w-0">
          <span className="font-medium">Arrange all pages</span>
          <span className="text-muted-foreground"> — {pages.length} {pages.length === 1 ? "page" : "pages"} in the merged file, in this order. Drag a page to move it, or use its arrows. Rotate or delete pages as needed.</span>
        </p>
        <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={onReset} data-testid="merge-arranger-reset">Start over</Button>
        <Button type="button" variant="outline" size="sm" className="h-8 shrink-0" onClick={onClose}>Done</Button>
      </div>
      {pages.length === 0 ? (
        <NoticeBox role="status" className="mt-3 text-sm">Every page was deleted. Press Start over to bring them back.</NoticeBox>
      ) : (
        <ol className="mt-3 flex flex-wrap gap-2 max-h-96 overflow-y-auto list-none p-0" data-testid="merge-arranger-grid">
          {pages.map((p, i) => (
            <ArrangedTile
              key={keys[i]}
              position={i}
              count={pages.length}
              page={p}
              fileName={files[p.fileIndex]?.name ?? "file"}
              render={render}
              dragging={dragFrom === i}
              over={dragOver === i && dragFrom !== null && dragFrom !== i}
              onDragStart={() => setDragFrom(i)}
              onDragOver={() => setDragOver(i)}
              onDrop={() => drop(i)}
              onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
              onMove={(to) => onChange(moveArranged(pages, i, to))}
              onTurn={(d) => onChange(turnArranged(pages, i, d))}
              onRemove={() => onChange(removeArranged(pages, i))}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function ArrangedTile({ position, count, page, fileName, render, dragging, over, onDragStart, onDragOver, onDrop, onDragEnd, onMove, onTurn, onRemove }: {
  position: number;
  count: number;
  page: ArrangedPage;
  fileName: string;
  render: (fileIndex: number, index: number) => Promise<string | null>;
  dragging: boolean;
  over: boolean;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
  onMove: (to: number) => void;
  onTurn: (d: 90 | -90) => void;
  onRemove: () => void;
}) {
  const ref = useRef<HTMLLIElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const started = useRef(false);
  const label = `Page ${position + 1}, ${fileName} page ${page.index + 1}`;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const start = () => {
      if (started.current) return;
      started.current = true;
      void render(page.fileIndex, page.index).then((u) => setUrl(u));
    };
    if (typeof IntersectionObserver === "undefined") { start(); return; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { start(); io.disconnect(); } }, { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [page.fileIndex, page.index, render]);

  return (
    <li
      ref={ref}
      className={`w-[96px] ${dragging ? "opacity-40" : ""}`}
      draggable
      data-testid="merge-arranger-tile"
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(position)); } catch { /* some browsers refuse */ } onDragStart(); }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      onDragEnd={onDragEnd}
    >
      <div
        role="img"
        aria-label={`${label}${page.rotate ? `, rotated ${page.rotate} degrees` : ""}`}
        className={`relative flex h-[116px] w-[96px] cursor-grab items-center justify-center overflow-hidden rounded border bg-muted/40 ${over ? "border-primary ring-2 ring-primary" : "border-border"}`}
      >
        {url ? (
          <img src={url} alt="" className="max-h-[108px] max-w-[88px] object-contain" style={{ transform: `rotate(${page.rotate}deg)`, ...(page.rotate % 180 ? { maxHeight: 88, maxWidth: 108 } : {}) }} draggable={false} />
        ) : (
          <span className="text-xs text-muted-foreground">{page.index + 1}</span>
        )}
      </div>
      <p className="mt-0.5 truncate text-center text-[11px] text-muted-foreground" title={fileName}>{position + 1}. {fileName.replace(/\.pdf$/i, "")} p{page.index + 1}</p>
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={position === 0} onClick={() => onMove(position - 1)} aria-label={`Move ${label} earlier`} title="Move earlier"><ArrowLeft className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" disabled={position === count - 1} onClick={() => onMove(position + 1)} aria-label={`Move ${label} later`} title="Move later"><ArrowRight className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="flex items-center justify-between">
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onTurn(-90)} aria-label={`Rotate ${label} left`} title="Rotate left"><RotateCcw className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onTurn(90)} aria-label={`Rotate ${label} right`} title="Rotate right"><RotateCw className="h-3.5 w-3.5" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onRemove} aria-label={`Delete ${label}`} title="Delete page"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </li>
  );
}
